import { Form, Select } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import {
   handleChangedRelatedField,
   handleGridTechname,
   renderDevInfo,
   handleFieldAlert,
} from "../../GeneralScripts";
import PropTypes from "prop-types";
import "./DictionaryField.less";
import FieldInfo from "../FieldInfo/FieldInfo";
import { getAllFieldsWithAlerts, handleTemplateAlert } from "../../../../util/Scripts";
import { MultiSelect } from "../../../BP2Components";

const { Option } = Select;

const createOptions = (element) => {
   let elementOptions = [];
   if (element) {
      // This adds empty option as the first element in options

      if (element.object.UIP_NUM4 === "1") {
         elementOptions.push({
            value: element.object.UIP_TEKST3,
            label: element.object.UIP_TEKST3,
            isPlaceholder: true,
         });
      }

      // In DictionaryField (element), we have two fields that come from the backend: options and sortedOptions.
      // I couldn’t find any information about when we should use options and when to use sortedOptions.
      // For now, Dominika told me to use options.
      // If someone knows the mystery of the DictionaryField, please write down the answer as to why we have both options and sortedOptions.

      //handling sortedOptions field
      // if (element.sortedOptions) {
      //    elementOptions = [
      //       ...elementOptions,
      //       ...element.sortedOptions.map((option) => ({
      //          label: Object.values(option)[0],
      //          value: Object.keys(option)[0]
      //       }))
      //    ];
      // }

      // handling options field
      if (element.options) {
         elementOptions = [
            ...elementOptions,
            ...Object.entries(element.options).map(([key, value]) => ({
               label: value,
               value: key,
               isPlaceholder: false,
            })),
         ];
      }
      if (
         element.value &&
         typeof element.value === "object" &&
         element.value.label &&
         !elementOptions.some((opt) => opt.label === element.value.label)
      ) {
         elementOptions = [
            {
               value: element.value.value,
               label: element.value.label,
               disabled: true,
               isPlaceholder: false,
            },
            ...elementOptions,
         ];
      }
   }
   return elementOptions;
};

const DictionaryField = ({
   isRequired,
   element,
   form,
   isVisible,
   disabled,
   formId,
   insideGrid = false,
   rowDataId,
   setChangelogData,
   initialValue,
   fieldInModal = false,
}) => {
   const {
      formData,
      changeFieldPendingStatus,
      fieldsInfo,
      setFieldsInfo,
      fieldOptions,
      changelog,
      fieldEnumMap,
   } = useFormContext();
   const intl = useIntl();
   const { getFieldDecorator } = form;
   const [options, setOptions] = useState([]);
   const [infoText, setInfoText] = useState(null);
   const initializedRef = useRef(false);

   const translatedLabel = element.label;
   const rawOptions = fieldOptions?.[element.tech_name] || [];

   const infoClass =
      fieldsInfo[element.tech_name] && fieldsInfo[element.tech_name] !== ""
         ? "select-field-info"
         : "";
   const emptyOptionClass =
      options[0]?.isPlaceholder && form.getFieldValue(element.tech_name) === options[0]?.label
         ? "select-option-placeholder"
         : "";

   const handleSelect = (e, event) => {
      if (isVisible(element) && setChangelogData && event?.target) {
         const isTargetHTML = event.target instanceof HTMLElement;
         isTargetHTML && setChangelogData(element.tech_name, e, element.label);
      }
      const allFieldsWithAlerts = getAllFieldsWithAlerts(formData?.forms_data?.tab?.[0]?.elements);
      handleChangedRelatedField(
         element,
         form,
         setFieldsInfo,
         allFieldsWithAlerts,
         formId,
         intl,
         formData._formKey,
         rowDataId,
         changeFieldPendingStatus,
         fieldEnumMap,
      );
      element.hasAlert &&
         handleFieldAlert(
            element.id,
            element.tech_name,
            form,
            formData._formKey,
            setFieldsInfo,
            fieldEnumMap,
         );
   };

   const convertValueToLabel = () => {
      const value = form.getFieldValue(element.tech_name);
      const validOption = options?.find((option) => option.value === value);
      if (validOption) {
         form.setFieldsValue({ [element.tech_name]: validOption.label });
      }
      //In other case it means that value was already correct ( the stored value is a label )
   };

   const getDefaultValue = () => {
      const normalize = (val) => {
         if (typeof val === "string") {
            return { value: val, label: val };
         }
         if (val && typeof val === "object") {
            return {
               value: val.value ?? val.label ?? "",
               label: val.label ?? val.value ?? "",
            };
         }
         return { value: "", label: "" };
      };

      let defaultValue;

      if (initialValue) {
         // 1) initialValue prop
         defaultValue = normalize(initialValue);
      } else if (element.value) {
         // 2) element.value
         const matchingOption =
            element.options &&
            Object.entries(element.options).find(([key, _]) => key === element.value);

         if (matchingOption) {
            const [optionValue, optionLabel] = matchingOption;
            const normalizedOption = { value: optionValue, label: optionLabel };
            defaultValue = normalize(normalizedOption);
         } else {
            defaultValue = normalize(element.value);
         }
      } else if (element.object?.UIP_NUM4 === "1") {
         // 3) special UIP_TEKST fallback
         defaultValue = normalize({
            value: element.object.UIP_TEKST3,
            label: element.object.UIP_TEKST3,
         });
      } else {
         // 4) nothing to show
         defaultValue = { value: "", label: "" };
      }

      if (isVisible(element) && defaultValue.label) {
         setChangelogData?.(element.tech_name, defaultValue.label);
      }

      return defaultValue;
   };

   useEffect(() => {
      convertValueToLabel();
   }, [form.getFieldValue(element.tech_name)]);

   useEffect(() => {
      const infoText = fieldsInfo[element.tech_name];
      setInfoText(infoText);
      handleTemplateAlert(element.tech_name, infoText);
   }, [fieldsInfo[element.tech_name]]);

   useEffect(() => {
      if (rawOptions.length !== 1) return;

      const current = form.getFieldValue(element.tech_name);
      if (current !== rawOptions[0]?.label) {
         form.setFieldsValue({ [element.tech_name]: rawOptions[0]?.label ?? "" });
      }
   }, [rawOptions, element.tech_name]);

   useEffect(() => {
      setOptions(createOptions(element));
   }, [element.tech_name]);

   useEffect(() => {
      if (options?.length > 0 && !element.value) {
         const firstOption = options?.[0];
         if (!initializedRef.current) {
            form.setFieldsValue({ [element.tech_name]: firstOption.label });
            setChangelogData?.(element.tech_name, firstOption.label, element.label);
         }
      }
   }, [options, element.tech_name]);

   return fieldInModal ? (
      <div style={{ width: "100%" }}>
         <span style={{ marginBottom: "1rem" }}>
            <span style={{ color: "red", marginRight: "10px" }}>*</span>
            {element?.label}:
         </span>
         <MultiSelect
            style={{
               width: "100%",
               backgroundColor: "#E2E8F0",
               borderColor: "red",
            }}
            onChange={(selectedOption) => {
               const newValue = selectedOption?.value || "";
               form.setFieldsValue({ [element.tech_name]: newValue });
               setChangelogData?.(element.tech_name, newValue, element.label);
            }}
            isMulti={false}
            options={options}
         ></MultiSelect>
      </div>
   ) : (
      <Form.Item
         key={element.tech_name}
         label={!insideGrid && translatedLabel}
         className={`${element?.type}-field`}
         style={{
            display: !isVisible(element) && !window.devLogged ? "none" : "",
            gridArea: handleGridTechname(element.tech_name),
            opacity: isVisible(element) ? "1" : "0.5",
         }}
      >
         {getFieldDecorator(element.tech_name, {
            initialValue: getDefaultValue()?.label || "",
            rules: [
               {
                  required: isRequired(element),
                  message: intl.formatMessage({ id: "field.required" }),
               },
            ],
         })(
            <Select
               showSearch
               className={`select-field__select field_${element.tech_name} ${emptyOptionClass} _type_${element?.type}_type_ _id_${element.id}_id_ ${infoClass} has-alert-${element.hasAlert}`}
               disabled={disabled}
               onSelect={(e) => {
                  handleSelect(e, event);
               }}
               filterOption={(input, option) =>
                  option?.props?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
               }
               dropdownClassName="dictionary-select"
            >
               {options?.map((option) => (
                  <Option
                     value={option.label}
                     key={option.value}
                     id={option.value}
                     disabled={option.disabled}
                  >
                     {option.label}
                  </Option>
               ))}
            </Select>,
         )}
         {renderDevInfo(element)}
         <FieldInfo text={infoText} />
      </Form.Item>
   );
};

DictionaryField.propTypes = {
   element: PropTypes.object.isRequired,
   disabled: PropTypes.bool,
   isVisible: PropTypes.func,
   form: PropTypes.any,
   formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   initialValue: PropTypes.string,
};
export default DictionaryField;
